import { useCallback, useState } from 'react';
import type { SyntheticEvent } from 'react';

import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TuneIcon from '@mui/icons-material/Tune';

import { EMPTY_ARRAY } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import type { Option } from '../MenuItem/types/option.type';

interface Params {
  readonly id: Id;
  readonly onStartCustomize?: (id: Id) => void;
  readonly onStartFlush?: (id: Id) => void;
  readonly onStartRemove?: (id: Id) => void;
  readonly onStartUpdate?: (id: Id) => void;
}

export const useActionMenu = ({
  id,
  onStartCustomize: handleStartCustomize,
  onStartFlush: handleStartFlush,
  onStartRemove: handleStartRemove,
  onStartUpdate: handleStartUpdate,
}: Readonly<Params>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isActionMenuOpen = Boolean(anchorEl);

  const handleOpenActionMenu = useCallback(
    (event: Readonly<SyntheticEvent<HTMLButtonElement>>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleCloseActionMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const startCustomize = useCallback(
    () => handleStartCustomize?.(id),
    [id, handleStartCustomize],
  );

  const startDelete = useCallback(
    () => handleStartRemove?.(id),
    [id, handleStartRemove],
  );

  const startFlush = useCallback(
    () => handleStartFlush?.(id),
    [id, handleStartFlush],
  );

  const startModify = useCallback(
    () => handleStartUpdate?.(id),
    [id, handleStartUpdate],
  );

  const options: readonly Option[] = [
    ...(handleStartUpdate
      ? [
          {
            action: startModify,
            children: (
              <>
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Modifier</ListItemText>
              </>
            ),
            key: 'modify',
            title: 'Modifier',
          },
        ]
      : EMPTY_ARRAY),
    ...(handleStartFlush
      ? [
          {
            action: startFlush,
            children: (
              <>
                <ListItemIcon>
                  <RestartAltIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Réinitialiser</ListItemText>
              </>
            ),
            key: 'flush',
            title: 'Réinitialiser',
          },
        ]
      : EMPTY_ARRAY),
    ...(handleStartCustomize
      ? [
          {
            action: startCustomize,
            children: (
              <>
                <ListItemIcon>
                  <TuneIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Spécifier</ListItemText>
              </>
            ),
            key: 'customize',
            title: 'Spécifier',
          },
        ]
      : EMPTY_ARRAY),
    ...(handleStartRemove
      ? [
          {
            action: startDelete,
            children: (
              <>
                <ListItemIcon>
                  <DeleteIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Supprimer</ListItemText>
              </>
            ),
            key: 'delete',
            title: 'Supprimer',
          },
        ]
      : EMPTY_ARRAY),
  ];

  return {
    anchorEl,
    handleOpenActionMenu,
    handleCloseActionMenu,
    isActionMenuOpen,
    options,
  };
};
